import { PropsWithChildren, useState, useEffect } from "react";
import * as runtime from "react/jsx-runtime";
import Twemoji from "react-twemoji";
import { MDXContent, MDXComponents } from "mdx/types";
import { EvaluateOptions, evaluate } from "@mdx-js/mdx";
import {
  PuzzleContentType,
  getPuzzleContentStaticDir,
} from "sockapi/scope-specs/puzzle-content-spec";
import { ScopeType } from "sockapi/scope-types";
import { useScopeStateResolvable } from "stores/ScopeStateStore";
import {
  Blanks,
  Flavor,
  PuzzleImage,
  PuzzleGallery,
  PuzzleAudio,
  Table,
  SpoilerText,
  AuthorsNotes,
  SolutionPieceGrid,
} from "components/PuzzleContent/puzzle-components";
import { PuzzleGrid } from "components/PuzzleContent/puzzle-grid";
import PuzzleGridInPiece from "components/PuzzleContent/puzzle-piece-grid";
import ClueListPiece from "components/PuzzleContent/ClueListPiece";
import TeamDisplayName from "components/TeamDisplayName";
import { Cutscene } from "components/PuzzleContent/Cutscene";
import { PuzzleContentPreviewWithLink } from "components/PuzzleContent/PuzzleContentPreview";
import { BlueTheatreShell } from "components/PuzzleContent/BlueTheatreShell";
import { FourPiecesPiece } from "components/PuzzleContent/FourPiecesPiece";
import {
  OneTimeUseTop,
  OneTimeUseBottom,
  OneTimeUseAll,
} from "components/PuzzleContent/OneTimeUseShell";
import { SpooflantuBox } from "components/PuzzleContent/Spooflantu";
import {
  TaxononogramyBig,
  TaxononogramySmall,
} from "components/PuzzleContent/Taxononogramy";
import { MultiverseOfMixupsShell } from "components/PuzzleContent/MultiverseOfMixupsShell";
import {
  SitePiecePreview,
  SitePieceList,
  TheWebFigure,
} from "components/PuzzleContent/TheWebSolution";
import { IslandFinder } from "components/PuzzleContent/IslandFinder";
import { Atoll, AtollTable } from "components/PuzzleContent/Atoll";
import { PuzzleLink } from "components/Link";
import {
  EndgameCorner,
  EndgamePiece,
  EndgameSolutionGrid,
} from "components/PuzzleContent/Endgame";
import { SurvivingTheOpenWorld } from "components/PuzzleContent/SurvivingTheOpenWorld";

type PuzzleContentProps = {
  puzName: string;
  staticPuzDir: string;
  firstKeyframeId?: string;
  answer?: string;
  isPreview: boolean;
};

const makePuzzleContentComponents = (
  props: PuzzleContentProps
): MDXComponents => {
  const { puzName, staticPuzDir, firstKeyframeId, answer, isPreview } = props;
  const Audio = (
    props: Omit<Parameters<typeof PuzzleAudio>[0], "staticPuzDir" | "isPreview">
  ) => (
    <PuzzleAudio staticPuzDir={staticPuzDir} isPreview={isPreview} {...props} />
  );
  return {
    pre: (props) => (
      <pre
        {...{
          "data-skip-inline-styles": "true",
        }}
      >
        {props.children}
      </pre>
    ),
    code: (props) => (
      <code
        {...{
          "data-skip-inline-styles": "true",
        }}
      >
        {props.children}
      </code>
    ),
    Flavor,
    Image: (props) => <PuzzleImage staticPuzDir={staticPuzDir} {...props} />,
    Gallery: (props) => (
      <PuzzleGallery staticPuzDir={staticPuzDir} {...props} />
    ),
    Audio,
    AConfusingJourneyText: (props: PropsWithChildren) => (
      <div className="confusing-journey-container">{props.children}</div>
    ),
    PuzzleGrid,
    PuzzleGridInPiece,
    ClueListPiece,
    Table,
    Blanks,
    Answer: (props: { index?: number }) => {
      const { index } = props;
      if (answer === undefined)
        throw new Error(`no answer found for ${puzName}`);
      if (index !== undefined) {
        return <SpoilerText>{answer.split(" / ")[index]}</SpoilerText>;
      }
      return <SpoilerText>{answer}</SpoilerText>;
    },
    SpoilerText,
    AuthorsNotes,
    SolutionPieceGrid,
    TeamDisplayName,
    Cutscene: () => {
      if (firstKeyframeId === undefined)
        throw new Error(`no firstKeyframeId found for ${puzName}`);
      return <Cutscene puzName={puzName} firstKeyframeId={firstKeyframeId} />;
    },
    PuzzleContentPreviewWithLink,
    PuzzleLink,
    Twemoji,
    BlueTheatreShell: (props) => (
      <BlueTheatreShell staticPuzDir={staticPuzDir} {...props} />
    ),
    FourPiecesPiece: (props) => (
      <FourPiecesPiece staticPuzDir={staticPuzDir} {...props} />
    ),
    SpooflantuBox,
    TaxononogramyBig,
    TaxononogramySmall,
    MultiverseOfMixupsShell,
    OneTimeUseTop: (props) => (
      <OneTimeUseTop staticPuzDir={staticPuzDir} {...props} />
    ),
    OneTimeUseBottom: (props) => (
      <OneTimeUseBottom staticPuzDir={staticPuzDir} {...props} />
    ),
    OneTimeUseAll: (props) => (
      <OneTimeUseAll staticPuzDir={staticPuzDir} {...props} />
    ),
    SitePiecePreview,
    SitePieceList,
    TheWebFigure,
    IslandFinder,
    Atoll,
    AtollTable,
    EndgameCorner,
    FourthWall: (props: { className?: string }) => (
      <div {...props} className={`fourth-wall ${props.className ?? ""}`} />
    ),
    SurvivingTheOpenWorld: (props) => (
      <SurvivingTheOpenWorld staticPuzDir={staticPuzDir} {...props} />
    ),
    EndgamePiece: (props) => <EndgamePiece puzName={puzName} {...props} />,
    RacingSoundsPiece: (props) => (
      <>
        <div className="racing-sounds">
          Facing North: <Audio name="N" />
        </div>
        <div className="racing-sounds">
          Facing East: <Audio name="E" />
        </div>
      </>
    ),
    EndgameSolutionGrid,
    TikTokSimulator: (props: { text?: string }) => (
      <a
        href={`${staticPuzDir}/default/tiktok.html`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "relative",
          zIndex: 1,
        }}
      >
        {props.text ?? "TikTok"}
      </a>
    ),
  };
};

export type PuzzleContentRenderContext = {
  elem: MDXContent;
  puzName: string;
  components: MDXComponents;
  props: PuzzleContentProps;
};

export const usePuzzleContent = (
  puzName: string,
  contentType: PuzzleContentType,
  isPreview?: boolean
) => {
  const scopeType: ScopeType = (() => {
    switch (contentType) {
      case PuzzleContentType.PUZZLE:
        return ScopeType.PUZZLE_CONTENT;
      case PuzzleContentType.SOLUTION:
        return ScopeType.SOLUTION_CONTENT;
    }
  })();
  const puzzleContent = useScopeStateResolvable(scopeType, {
    puzName,
  });

  // Track all dependencies of the puzzle content element in the
  // same state to ensure that they all update together.
  const [renderCtx, setRenderCtx] = useState<PuzzleContentRenderContext | null>(
    null
  );

  useEffect(() => {
    if (puzzleContent === null) {
      setRenderCtx(null);
      return;
    }

    const { content, firstKeyframeId, answer } = puzzleContent;
    const staticPuzDir = getPuzzleContentStaticDir(puzzleContent);
    const props: PuzzleContentProps = {
      puzName,
      staticPuzDir,
      firstKeyframeId,
      answer: answer ?? undefined,
      isPreview: isPreview ?? false,
    };
    const components = makePuzzleContentComponents(props);

    let isCanceled = false;
    // There seems to be an incompatibility with MDX and our React
    // version's JSX runtime type, but explicitly casting seems to
    // be fine.
    evaluate(content, runtime as EvaluateOptions)
      .then((module) => {
        if (isCanceled) return;
        setRenderCtx({
          elem: module.default,
          puzName,
          components,
          props,
        });
      })
      .catch((err) => {
        console.error(err);

        if (isCanceled) return;
        setRenderCtx(null);
      });

    return () => {
      isCanceled = true;
    };
  }, [puzzleContent, puzName, isPreview]);

  return {
    puzzleContent,
    renderCtx:
      renderCtx !== null && renderCtx.puzName === puzName ? renderCtx : null,
  };
};
